import { OrderTypeEnum } from '@/stores/useOrderTypeStore';
import { parsePhoneNumber } from '@/utils';
import type { BasicProduct, Order } from '@/types/order';

/**
 * Build the Ant Design form values used to re-hydrate the Add New Order form
 * when the merchant navigates back from carrier selection. Extracted as a pure
 * function so back-nav restoration is unit-testable. CRITICAL: orderType must be
 * restored here — omitting it silently reverts a RETURN/EXCHANGE order to NORMAL.
 */
export function buildRestoreFormValues(payload: Order, savedProvinceCode?: string | null) {
  const contact = payload.contact;
  const phoneInfo = parsePhoneNumber(contact?.phone);
  const secondaryPhoneInfo = parsePhoneNumber(contact?.additional_phones?.[0]);

  return {
    name: contact?.full_name,
    phoneNumberPrimary: phoneInfo.phone || '',
    countryCodePrimary: phoneInfo.countryCode,
    secondaryPhoneNumber: secondaryPhoneInfo?.phone,
    countryCodeSecondary: secondaryPhoneInfo?.countryCode,
    email: contact?.email,
    province: savedProvinceCode || undefined,
    region_uid: contact?.region_uid,
    address: contact?.address_text,
    deliveryInstruction: payload.description,
    orderId: payload.custom_id,
    packageSize: payload.size,
    // The service picked on this order; without it BACK re-quoted on the
    // standing rules and the next summary priced a different service.
    serviceCode: payload.service_code,
    // Multi-box: without this, stepping back from carrier/summary silently
    // turned an N-box order into one box, and every later price was for one box.
    // Only a real multi-box list (2+) is restored; single-box stays keyless.
    ...(Array.isArray(payload.boxes) && payload.boxes.length >= 2
      ? { boxes: payload.boxes.map((box) => ({ size: box.size })) }
      : {}),
    // Fix: order type was missing here — restore it (default to NORMAL).
    orderType: (payload.type as OrderTypeEnum) ?? OrderTypeEnum.NORMAL,
    isFragile: payload.is_fragile,
    // Same reason as isFragile above: without it, ticking "temperature control"
    // and stepping into carrier selection loses the flag on the way back.
    temperatureControl: payload.temperature_control,
    CodOrPrepaid: payload.payment_type,
    CustomerOrMerchant: payload.shipment_fee_type,
    pickupOrDropoff: payload.pick_up_type,
    saved_pick_up_address_uid: payload.saved_pick_up_address_uid,
    pick_up_address_uid: payload.pick_up_address_uid,
  };
}

/**
 * Rebuild the product store list when restoring on back-nav. CRITICAL: preserve
 * `is_fragile` (so the order's fragility survives — the auto-derive effect keys
 * off product fragility) plus `uid`/`product_catalog_uid` (so catalog linkage is
 * not lost). Dropping these previously reset fragility to its default on back-nav.
 */
export function buildRestoreProducts(products: Order['products']): BasicProduct[] {
  return (products ?? []).map((p) => ({
    title: p.title || '',
    quantity: String(p.quantity || '1'),
    price: String(p.price || ''),
    is_fragile: p.is_fragile ?? false,
    uid: p.uid ?? undefined,
    product_catalog_uid: p.product_catalog_uid ?? undefined,
  }));
}
